import { useEffect, useState } from "react";
import { useApi } from "../../../api/useApi";
import { availableDataSourcesForBP } from "../../../api/dataSourceQueries";

// Builds og -> target source pairings from a blueprint (primary sources + any cross-table sources),
// defaulting each target to a same-named source in this environment. Shared by the Mappings and
// Deploy All flows. Returns the pairings, the available target-source options, a `ready` flag, a
// setter to override a single pairing's target, and a helper that shapes the pairings for the
// applyBlueprint mutation.
//
// `ready` reports that pairing resolution has SETTLED, which is not the same as "there are pairings".
// Callers must gate their loading state on it rather than on `pairings.length`: a blueprint with no
// sourceProfiles (or an available-sources query that comes back empty) legitimately yields zero
// pairings, and gating on the count leaves such a blueprint stuck behind a spinner forever.
export function useSourcePairings(blueprint) {
  const [pairings, setPairings] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [ready, setReady] = useState(false);

  const [{ data: availableSources, loading: sourcesLoading }] = useApi(
    availableDataSourcesForBP,
    {
      first: 9999,
      where: { enabled: { eq: true } },
      tagFilter: { exactMatch: false, tagIds: [] },
    }
  );

  const sourcesAvail = availableSources?.availableDataSources?.edges ?? [];
  const hasProfiles = !!blueprint?.sourceProfiles?.length;

  useEffect(() => {
    // Nothing to pair -- settle immediately without waiting on the sources query.
    if (!hasProfiles) {
      setPairings([]);
      setSourceOptions([]);
      setReady(true);
      return;
    }

    // Still waiting on the target sources; stay unsettled so callers keep showing their spinner.
    if (sourcesLoading) {
      return;
    }

    const built = [];
    const seen = new Set();
    const add = (name, ogId) => {
      if (!name || seen.has(name)) {
        return;
      }
      seen.add(name);
      const found = sourcesAvail.find((s) => s?.node?.name === name);
      built.push({
        ogSource: { name, sourceId: ogId },
        newSource: { name: found?.node?.name, sourceId: found?.node?.id },
      });
    };

    blueprint.sourceProfiles.forEach((ogs) => {
      add(ogs.ogName, ogs.ogId);
      ogs?.ogCrossTableReq?.forEach((xt) => add(xt.name, xt.id));
    });

    setPairings(built);
    setSourceOptions(
      sourcesAvail.map((s) => ({ label: s.node?.name, value: s.node?.id }))
    );
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSources, sourcesLoading, hasProfiles, blueprint]);

  const setPairingTarget = (index, option) => {
    setPairings((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        newSource: { name: option?.label || "", sourceId: option?.value || "" },
      };
      return updated;
    });
  };

  // og -> target pairs (only those with a chosen target) shaped for the applyBlueprint mutation.
  const toSourcePairings = () =>
    pairings
      .filter((p) => p?.newSource?.name)
      .map((p) => ({ ogName: p.ogSource.name, targetName: p.newSource.name }));

  return { pairings, sourceOptions, ready, setPairingTarget, toSourcePairings };
}
